import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Html5QrcodeScanner } from "html5-qrcode";
import { t } from "ttag";
import { BaseDialog, ChevronLeft, IconButton, Typography } from "@coolremote/shared";
import { useStoreActions } from "../../models/RootStore";
import { DeviceAccountInfo } from "../../models/deviceAccountInfo";
import {
  isQaBypassCredentials,
  QA_BYPASS_LOCATION
} from "../../models/devicePublicRegistrationTestBypass";
import assetsService from "../../services/assetsService";
import { DeviceCredentialsForm } from "../RegisterNewDevice/DeviceCredentialsForm";
import { parseDeviceQrCode } from "../RegisterNewDevice/deviceQrCode";
import { DeviceRegistrationLocationState } from "./deviceRegistrationLocationState";
import { clearStalePublicRegistrationToken } from "./publicRegistrationSession";
import QRImg from "../../images/QR.png";
import styles from "./DeviceRegistrationPublic.module.css";

const qrCodeRegionId = "public-register-device-qr-region";

/** The SDK does not export a typed error shape; narrow just enough to read `.message` safely. */
const getErrorMessage = (error: unknown): string | undefined =>
  error instanceof Error ? error.message : undefined;

/**
 * Step 1 of the public (pre-login) device registration flow — CM-168.
 *
 * Reached from the "Register Now" link on the mobile login screen (CM-615), so it
 * stays outside PrivateRoute: the technician has no account yet at this point.
 *
 * The credentials form itself (serial / PIN / QR / location / confirm) is
 * DeviceCredentialsForm, extracted from the authenticated add-device screen in
 * CM-615 and rendered here as-is (still Material UI — see CM-642). This component
 * owns the surrounding chrome, the field state, and the validation.
 */
const DeviceRegistrationWizard: React.FC = () => {
  const history = useHistory<DeviceRegistrationLocationState | undefined>();
  const getAccountByDeviceSerialForPublicRegistration = useStoreActions(
    (actions) => actions.devices.getAccountByDeviceSerialForPublicRegistration
  );

  // Seeded from location.state so that navigating here via the back arrow from
  // CM-617's user-selection screen restores what was typed, instead of
  // remounting to a blank form — see handleSubmit, which writes this state back
  // onto the current history entry before pushing forward.
  const restoredState = history.location.state;

  const [serial, setSerial] = useState<string>(restoredState?.serial ?? "");
  const [pin, setPin] = useState<string>(restoredState?.pin ?? "");
  const [description, setDescription] = useState<string>(restoredState?.description ?? "");
  const [confirmChecked, setConfirmChecked] = useState<boolean>(restoredState?.confirmChecked ?? false);
  const [isCameraOpen, setIsCameraOpen] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const logoSrc = assetsService.getResourcePath("logo.png");

  // This is the wizard's entry step — reached fresh, or by backing out of a
  // prior attempt. Either way, any token set by an earlier registration or
  // login that never finished is stale by now; drop it before the next
  // attempt's first API call picks it up.
  useEffect(() => {
    clearStalePublicRegistrationToken();
  }, []);

  useEffect(() => {
    if (!isCameraOpen) {
      return undefined;
    }

    const scanner = new Html5QrcodeScanner(qrCodeRegionId, { fps: undefined }, false);
    scanner.render(handleScanSuccess, undefined);

    return () => {
      scanner.clear().catch((error) => {
        console.error("Failed to clear html5QrcodeScanner. ", error);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCameraOpen]);

  /**
   * TEST-ONLY: once the QA bypass serial/PIN are both typed, auto-fill the rest
   * of the form so it can be submitted without touching anything else. Kept as
   * its own effect, separate from the real onSerialChange/onPinChange handlers,
   * so this stays purely additive on top of the normal form behaviour. See
   * `devicePublicRegistrationTestBypass.ts`.
   */
  useEffect(() => {
    if (!isQaBypassCredentials(serial, pin)) {
      return;
    }
    applyQaBypassAutoFill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serial, pin]);

  const applyQaBypassAutoFill = () => {
    setDescription(QA_BYPASS_LOCATION);
    setConfirmChecked(true);
  };

  const handleToggleCamera = () => {
    setIsCameraOpen(!isCameraOpen);
  };

  const handleScanSuccess = (qrCode: string | null) => {
    if (!qrCode) {
      return;
    }
    const scanned = parseDeviceQrCode(qrCode);
    setSerial(scanned.serial);
    setPin(scanned.pin);
    setIsCameraOpen(false);
  };

  const handleConfirmToggle = () => {
    setConfirmChecked(!confirmChecked);
  };

  const handleDismissError = () => {
    setErrorMessage(null);
  };

  const getValidationError = (deviceInfo: DeviceAccountInfo | null): string | null => {
    if (!deviceInfo || !deviceInfo.isFound) {
      return t`The serial you entered was not found. Please verify you entered it correctly`;
    }
    if (!deviceInfo.isAuthenticated) {
      return t`The serial and pin do not match. Please verify you entered it correctly`;
    }
    if (!deviceInfo.isConnected) {
      return t`The device is not connected. It must be connected to be registered. Please connect it and try again`;
    }
    if (deviceInfo.isRegistered) {
      return t`The device is already registered to another account, and can not be registered again`;
    }
    if (deviceInfo.isBlocked) {
      return t`This device can't be registered. Please contact CoolAutomation support`;
    }
    if (!deviceInfo.hasProfessionalPackage) {
      return t`A Professional licence is not available for this device. Please contact CoolAutomation support to add one before registering.`;
    }
    return null;
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitDisabled) {
      return;
    }
    handleSubmit();
  };

  const handleSubmit = async () => {
    const trimmedSerial = serial.replace(/\s+/g, " ").trim();
    const trimmedPin = pin.replace(/\s+/g, " ").trim();

    setIsValidating(true);
    try {
      const deviceInfo: DeviceAccountInfo = await getAccountByDeviceSerialForPublicRegistration({
        serial: trimmedSerial,
        pin: trimmedPin
      });

      const validationError = getValidationError(deviceInfo);
      if (validationError) {
        setErrorMessage(validationError);
        setIsValidating(false);
        return;
      }

      // Attach the typed values to *this* history entry (not just the next one),
      // so that going back from user-selection lands here with the form restored
      // instead of blank.
      history.replace(history.location.pathname, {
        serial: trimmedSerial,
        pin: trimmedPin,
        description,
        confirmChecked
      });
      history.push("/register-device/user-selection", {
        serial: trimmedSerial,
        pin: trimmedPin,
        description
      });
      // No setIsValidating(false) here: the push above unmounts this component,
      // and calling it afterwards logs a "state update on an unmounted
      // component" warning.
    } catch (error: unknown) {
      setErrorMessage(getErrorMessage(error) || t`Something went wrong. Please try again`);
      setIsValidating(false);
    }
  };

  const handleBackToLogin = () => {
    history.push("/");
  };

  const isSubmitDisabled =
    isValidating || !serial.length || !pin.length || !confirmChecked;

  return (
    <div className={styles.screen}>
      <div className={styles.appBar}>
        <IconButton
          className={styles.backButton}
          onClick={handleBackToLogin}
          aria-label={t`Back to login`}
        >
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={styles.appBarTitleLink}>{t`New Device Registration`}</span>
        <span />
      </div>

      <div className={styles.logoBanner}>
        <img src={logoSrc} alt={t`CoolAutomation`} />
      </div>

      <div className={styles.formContent}>
        <Typography variant="pageTitle" className={styles.formHeading}>
          {t`Lets connect your HVAC Device`}
        </Typography>

        <form onSubmit={handleFormSubmit}>
          <DeviceCredentialsForm
            values={{ serial, pin, description }}
            errors={{}}
            onSerialChange={setSerial}
            onPinChange={setPin}
            onDescriptionChange={setDescription}
            isCameraOpen={isCameraOpen}
            onToggleCamera={handleToggleCamera}
            qrCodeRegionId={qrCodeRegionId}
            qrImageSrc={QRImg}
            confirmChecked={confirmChecked}
            onConfirmToggle={handleConfirmToggle}
            isSubmitDisabled={isSubmitDisabled}
          />
        </form>
      </div>

      <BaseDialog
        open={!!errorMessage}
        onClose={handleDismissError}
        onConfirm={handleDismissError}
        title={t`Device registration error`}
        confirmLabel={t`OK`}
        hideCancel
      >
        <Typography>{errorMessage}</Typography>
      </BaseDialog>
    </div>
  );
};

export default DeviceRegistrationWizard;
